import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL, API_BASE } from '@/config/api';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Star, MapPin, Phone, Mail, Building2, Stethoscope, Award, Calendar, ExternalLink, Search, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useVaniForm } from '@/voice/useVaniForm';
import CarePageHeader from '@/components/CarePageHeader';
import { sortHospitalsByRegion } from '@/lib/careDirectory';
import MetaTags from '@/components/SEO/MetaTags';

const finalBackendUrl = BACKEND_URL;
const finalApi = API_BASE;

function Fact({ label, value }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b border-hair last:border-0">
      <dt className="text-xs sm:text-sm text-charcoal sm:w-44 shrink-0">{label}</dt>
      <dd className={`text-sm sm:text-base ${empty ? 'text-charcoal' : 'text-forest-ink'}`}>
        {empty ? 'Not available' : value}
      </dd>
    </div>
  );
}

function HospitalFacts({ hospital }) {
  return (
    <dl className="hospital-facts rounded-2xl border border-hair bg-mist/70 px-4 sm:px-5">
      <Fact label="Designation" value={hospital.designation} />
      <Fact label="Accreditation" value={hospital.accreditation?.join(', ')} />
      <Fact label="Established" value={hospital.established_year} />
      <Fact label="Total beds" value={hospital.total_beds} />
      <Fact label="Languages" value={hospital.languages?.join(', ')} />
      <Fact label="Insurance" value={hospital.insurance_schemes?.join(', ')} />
      <Fact
        label="International patients"
        value={hospital.international_patients ? 'Accepted' : null}
      />
    </dl>
  );
}

function DetailTile({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2.5 rounded-2xl border border-hair bg-paper px-3.5 py-3 min-h-[48px]">
      {Icon ? <Icon className="w-4 h-4 text-forest-ink shrink-0" /> : null}
      <span className="text-sm text-forest-ink leading-snug">{children}</span>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return String(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function Signal({ label, value }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="rounded-2xl border border-hair bg-paper p-3.5">
      <p className="text-xs text-charcoal">{label}</p>
      <p className={`text-sm sm:text-base font-semibold mt-1 ${empty ? 'text-forest-ink/70 italic' : 'text-forest-ink'}`}>
        {empty ? 'Not available' : value}
      </p>
    </div>
  );
}

function LiveSignals({ signals }) {
  if (!signals?.is_live) {
    return (
      <div className="rounded-2xl border border-hair bg-mist/70 p-4 text-sm text-charcoal leading-relaxed">
        Live bed availability and wait times are not yet connected for this centre.
        Contact the hospital directly for current capacity.
      </div>
    );
  }
  return (
    <div className="live-signals space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        <Signal label="Beds available" value={signals.beds_available} />
        <Signal
          label="Typical wait"
          value={signals.wait_time_days != null ? `${signals.wait_time_days} days` : null}
        />
        <Signal label="Queue" value={signals.queue_status} />
      </div>
      <p className="text-xs text-forest-ink">Updated {formatRelative(signals.last_updated)}</p>
    </div>
  );
}

function DoctorCard({ doctor }) {
  const name = doctor.display_name || doctor.name;
  return (
    <div className="bg-keylime-wash rounded-lg p-4 border border-border-mist hover:border-sage-mist transition-all">
      <h4 className="text-forest-ink font-semibold text-lg">{name}</h4>
      {(doctor.role_title || doctor.specialization) && (
        <p className="text-forest-ink text-sm mt-1">{doctor.role_title || doctor.specialization}</p>
      )}
      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-charcoal">
        {doctor.qualifications && (
          <div className="flex items-center gap-1">
            <Award className="w-4 h-4" />
            {doctor.qualifications}
          </div>
        )}
        {doctor.experience != null && (
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {doctor.experience} years exp.
          </div>
        )}
      </div>
      {doctor.source_url ? (
        <a
          href={doctor.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-3 text-sm text-forest-ink hover:text-forest-shadow"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View source
          {doctor.verified_on ? ` · Verified ${doctor.verified_on}` : ''}
        </a>
      ) : (
        <p className="text-xs text-forest-ink/70 italic mt-3">Source not listed</p>
      )}
    </div>
  );
}

function DoctorsTab({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="text-center py-8">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-forest-ink border-r-transparent" />
        <p className="text-charcoal mt-4">Loading doctors...</p>
      </div>
    );
  }

  if (data.doctors?.length > 0) {
    return (
      <div className="space-y-3">
        {data.doctors.map((d) => (
          <DoctorCard key={d.id || d.name} doctor={d} />
        ))}
        {data.withheld_pending_verification > 0 && (
          <p className="text-xs text-forest-ink pt-2">
            {data.withheld_pending_verification} further listing(s) pending
            verification against the hospital&apos;s own directory.
          </p>
        )}
      </div>
    );
  }

  const info = data.department_info;
  if (!info) {
    return (
      <div className="text-center py-8">
        <p className="text-charcoal">
          Individual clinician profiles are added only once confirmed against the hospital&apos;s own directory.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-charcoal text-sm leading-relaxed">{info.message}</p>
      <dl className="rounded-2xl border border-hair bg-mist/70 px-4 sm:px-5">
        <Fact label="Designation" value={info.designation} />
        <Fact label="Services" value={info.services?.join(', ')} />
        <Fact label="Languages" value={info.languages?.join(', ')} />
      </dl>
      {info.international_desk && (
        <Badge className="bg-cream-paper text-forest-ink">International patient desk</Badge>
      )}
      {info.referral_route ? (
        <a
          href={info.referral_route}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper font-normal py-2.5 px-4 rounded-lg transition-all"
        >
          <ExternalLink className="w-4 h-4" />
          How to get referred here
        </a>
      ) : (
        <p className="text-sm text-charcoal leading-relaxed">
          Ask your oncologist for a referral to this centre.
        </p>
      )}
    </div>
  );
}

const SELECT_CONTENT_CLASS =
  'bg-cream-paper border border-sage-mist text-forest-ink p-1.5 z-[9999] max-h-[300px] overflow-y-auto shadow-lg';
const SELECT_ITEM_CLASS =
  'text-forest-ink cursor-pointer px-3 py-2.5 my-0.5 rounded-md focus:bg-keylime-wash focus:text-forest-ink focus:outline-none data-[highlighted]:bg-keylime-wash data-[highlighted]:text-forest-ink';
const TAG_CLASS =
  'bg-cream-paper text-forest-ink border border-sage-mist text-xs font-medium px-2.5 py-1 leading-tight';
const TAG_MORE_CLASS =
  'bg-mint-veil text-forest-ink border border-sage-mist text-xs font-medium px-2.5 py-1 leading-tight';

function hospitalInitials(name) {
  const skip = new Set(['the', 'and', 'of', 'for', '&', 'pvt', 'ltd', 'limited', 'inc']);
  const parts = String(name || '')
    .replace(/[()[\]{},./\\-]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ''))
    .filter((w) => w && !skip.has(w.toLowerCase()));
  if (parts[0] && parts[0].length <= 4 && parts[0] === parts[0].toUpperCase()) {
    return parts[0].slice(0, 3);
  }
  return parts.slice(0, 3).map((w) => w[0]).join('').toUpperCase() || 'H';
}

function TagList({ items, empty = 'Not available', max = 3 }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) {
    return <p className="text-sm text-charcoal min-h-[2.25rem]">{empty}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5 min-h-[2.25rem] content-start">
      {list.slice(0, max).map((item) => (
        <Badge key={item} className={TAG_CLASS}>
          {item}
        </Badge>
      ))}
      {list.length > max && (
        <Badge className={TAG_MORE_CLASS}>+{list.length - max} more</Badge>
      )}
    </div>
  );
}

const FindHospitals = () => {
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [cancerTypes, setCancerTypes] = useState([]);
  const [doctorPayloads, setDoctorPayloads] = useState({});
  const [selectedCity, setSelectedCity] = useState('All');
  const [selectedCancerType, setSelectedCancerType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);

  const [cities, setCities] = useState(['All']);

  useVaniForm('/find-hospitals', {
    fill: (fields) => {
      if (fields.query != null) setSearchQuery(String(fields.query));
      if (fields.city != null) setSelectedCity(String(fields.city));
      if (fields.cancerType != null) setSelectedCancerType(String(fields.cancerType));
    },
    search: () => {},
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setStatsError(false);
      const params = {};
      if (selectedCity !== 'All') params.city = selectedCity;
      if (selectedCancerType !== 'All') params.cancer_type = selectedCancerType;

      const [hospitalsRes, cancerTypesRes, statsRes, citiesRes] = await Promise.all([
        axios.get(`${finalApi}/hospitals`, { params }),
        axios.get(`${finalApi}/cancer-types`),
        axios.get(`${finalApi}/stats`).catch(() => null),
        axios.get(`${finalApi}/cities`).catch(() => null)
      ]);

      // Update cities list from backend
      if (citiesRes?.data?.cities) {
        setCities(['All', ...citiesRes.data.cities]);
      }

      // Set hospitals - ensure it's an array
      const hospitalsData = Array.isArray(hospitalsRes.data) ? hospitalsRes.data : [];
      setHospitals(hospitalsData);
      
      // Handle both array and object formats for cancer types
      let cancerTypesData = [];
      if (Array.isArray(cancerTypesRes.data)) {
        cancerTypesData = cancerTypesRes.data;
      } else if (cancerTypesRes.data?.all_cancers) {
        cancerTypesData = cancerTypesRes.data.all_cancers;
      } else if (cancerTypesRes.data?.rare_cancers || cancerTypesRes.data?.common_cancers) {
        cancerTypesData = [
          ...(cancerTypesRes.data.rare_cancers || []),
          ...(cancerTypesRes.data.common_cancers || [])
        ];
      }
      setCancerTypes(cancerTypesData);

      // Honest stats only, never invent fallbacks. Skeleton while loading; nothing on error.
      if (statsRes?.data) {
        setStats({
          hospitals: statsRes.data.hospitals_mapped ?? null,
          cancerTypes: statsRes.data.cancer_types_supported ?? null,
          cities: statsRes.data.cities_covered ?? null,
          rareTotal: statsRes.data.rare_cancers ?? statsRes.data.counts?.rare_total ?? null,
        });
      } else {
        setStats(null);
        setStatsError(true);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setHospitals([]);
      setCancerTypes([]);
      setStats(null);
      setStatsError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedCancerType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchDoctors = async (hospitalId) => {
    if (doctorPayloads[hospitalId]) return;

    try {
      const response = await axios.get(`${finalApi}/hospitals/${hospitalId}/doctors`);
      const payload = response.data;
      if (Array.isArray(payload)) {
        setDoctorPayloads(prev => ({
          ...prev,
          [hospitalId]: { doctors: payload, withheld_pending_verification: 0, department_info: null },
        }));
        return;
      }
      setDoctorPayloads(prev => ({
        ...prev,
        [hospitalId]: {
          doctors: payload?.doctors || [],
          withheld_pending_verification: payload?.withheld_pending_verification || 0,
          department_info: payload?.department_info || payload?.department || null,
        },
      }));
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctorPayloads(prev => ({
        ...prev,
        [hospitalId]: { doctors: [], withheld_pending_verification: 0, department_info: null },
      }));
    }
  };

  const filteredHospitals = sortHospitalsByRegion(
    hospitals.filter((hospital) =>
      hospital.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hospital.city?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <>
    <style>{`
      .hospital-search,
      .hospital-search * { box-sizing: border-box; }
      .hospital-search button,
      .hospital-search [role="combobox"],
      .hospital-search input {
        min-width: 0 !important;
        min-height: 0 !important;
      }
      .hospital-search [data-testid="search-button"] {
        color: #fffefc !important;
        background: #0f3e17 !important;
      }
      .hospital-segment [role="tab"] {
        min-width: 0 !important;
        min-height: 40px !important;
        padding: 0 6px !important;
        color: #0f3e17 !important;
        background: transparent !important;
        box-shadow: none !important;
      }
      .hospital-segment [role="tab"][data-state="active"] {
        background: #ffffff !important;
        color: #0f3e17 !important;
        box-shadow: 0 1px 2px rgba(14, 17, 22, 0.08) !important;
      }
      .hospital-segment [role="tab"][data-state="active"] * {
        color: #0f3e17 !important;
      }
    `}</style>
    <div className="care-page min-h-screen bg-cream-paper relative">
      <MetaTags
        title="Find US Cancer Hospitals & NCI Centers"
        description="Search 160+ verified US cancer centers and NCI-designated hospitals. Filter by cancer type, city, and accepted insurance."
        path="/find-hospitals"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 z-10">
          <CarePageHeader
            current="/find-hospitals"
            title="Find cancer hospitals"
            titleTestId="page-title"
            ledeTestId="page-subtitle"
            lede={
              !stats && !statsError ? (
                <span className="inline-block h-5 w-64 max-w-full bg-sage-mist animate-pulse rounded align-middle" aria-hidden="true" />
              ) : stats?.cities != null ? (
                `Search verified cancer centers across ${stats.cities} cities.`
              ) : (
                'Search verified cancer centers by city, name, or cancer type.'
              )
            }
          />

          <div className="max-w-3xl mx-auto mb-6 rounded-[22px] border border-hair bg-mist/60 px-4 py-4 sm:px-5 text-left">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-forest-ink">Match my case</p>
                <p className="text-xs text-charcoal mt-1 leading-relaxed">
                  Rank verified centres by cancer type and city, same filters as the directory, with a short case-fit note.
                </p>
              </div>
              <Link
                to={`/get-matched?cancer_type=${encodeURIComponent(selectedCancerType !== 'All' ? selectedCancerType : '')}&city=${encodeURIComponent(selectedCity !== 'All' ? selectedCity : '')}${selectedCancerType !== 'All' ? '&autostart=1' : ''}`}
                className="inline-flex items-center justify-center gap-2 min-h-11 px-5 rounded-full bg-forest-ink text-cream-paper text-sm shrink-0"
              >
                Open matching
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="text-center relative z-20">
            
            {/* Search Bar */}
            <div className="hospital-search max-w-3xl mx-auto relative z-20">
              <div className="rounded-[22px] bg-paper border border-hair p-3 sm:p-3.5 space-y-3">
                <label className="flex items-center gap-3 min-w-0 h-12 px-3 rounded-2xl bg-mist">
                  <Search className="w-4 h-4 text-charcoal shrink-0" aria-hidden="true" />
                  <Input
                    type="text"
                    placeholder="Search hospitals or cities"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') fetchData();
                    }}
                    className="flex-1 min-w-0 h-full border-0 bg-transparent shadow-none rounded-none px-0 text-forest-ink placeholder:text-charcoal text-sm sm:text-base focus-visible:ring-0 focus-visible:border-0"
                    data-testid="search-input"
                  />
                </label>
                <div className="grid grid-cols-1 min-[640px]:grid-cols-[minmax(0,1fr)_minmax(200px,1fr)_auto] gap-2">
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger
                      className="h-12 w-full min-w-0 border border-hair bg-paper rounded-2xl px-3 text-forest-ink focus:ring-0 cursor-pointer [&>svg]:text-charcoal [&>svg]:opacity-100 [&>svg]:shrink-0 text-sm [&>span]:line-clamp-none"
                      data-testid="city-filter"
                    >
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{selectedCity === 'All' ? 'All cities' : selectedCity}</span>
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLASS} position="popper">
                      {cities.map((city) => (
                        <SelectItem key={city} value={city} className={SELECT_ITEM_CLASS}>
                          {city === 'All' ? 'All cities' : city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedCancerType} onValueChange={setSelectedCancerType}>
                    <SelectTrigger
                      className="h-12 w-full min-w-[11rem] border border-hair bg-paper rounded-2xl px-3 text-forest-ink focus:ring-0 cursor-pointer [&>svg]:text-charcoal [&>svg]:opacity-100 [&>svg]:shrink-0 text-sm [&>span]:line-clamp-none"
                      data-testid="cancer-type-filter"
                    >
                      <span className="whitespace-nowrap">
                        {selectedCancerType === 'All' ? 'All types' : selectedCancerType}
                      </span>
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLASS} position="popper">
                      <SelectItem value="All" className={SELECT_ITEM_CLASS}>All types</SelectItem>
                      {loading ? (
                        <SelectItem value="loading" disabled className="text-charcoal px-3 py-2.5">
                          Loading cancer types...
                        </SelectItem>
                      ) : cancerTypes.length > 0 ? (
                        cancerTypes.map((type) => (
                          <SelectItem key={type.id || type.name} value={type.name} className={SELECT_ITEM_CLASS}>
                            {type.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled className="text-charcoal px-3 py-2.5">
                          No data available. Please seed the database by calling POST /api/seed-data
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    className="bg-forest-ink hover:bg-forest-shadow text-cream-paper h-12 px-6 rounded-2xl font-medium text-sm w-full min-[520px]:w-auto"
                    onClick={fetchData}
                    data-testid="search-button"
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
            {/* Stats: skeleton while loading; hide entirely if /api/stats failed. No doctor count until verified records exist. */}
            {stats ? (
              <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-4xl mx-auto mt-8 sm:mt-10 px-2">
                <div className="bg-paper rounded-2xl p-4 border border-hair">
                  <div className="care-stat-value">{stats.hospitals}</div>
                  <div className="care-stat-label">Hospitals</div>
                </div>
                <div className="bg-paper rounded-2xl p-4 border border-hair">
                  <div className="care-stat-value">{stats.cancerTypes}</div>
                  <div className="care-stat-label">Cancer types</div>
                </div>
                <div className="bg-paper rounded-2xl p-4 border border-hair">
                  <div className="care-stat-value">{stats.cities}</div>
                  <div className="care-stat-label">Cities</div>
                </div>
              </div>
            ) : statsError ? null : (
              <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-4xl mx-auto mt-8 sm:mt-10 px-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-cream-paper rounded-lg sm:rounded-xl p-4 sm:p-6 border border-sage-mist">
                    <div className="h-9 sm:h-10 w-16 bg-mint-veil animate-pulse rounded" />
                    <div className="h-4 w-24 bg-mint-veil animate-pulse rounded mt-3" />
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>

      {/* Hospitals List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 relative z-10 isolate">
        {!loading && hospitals.length === 0 && (
          <div className="text-center py-12 bg-keylime-wash border border-border-mist rounded-xl p-8 mb-8">
            <p className="text-xl text-charcoal mb-4">⚠️ No Hospitals Found</p>
            <p className="text-charcoal mb-4">
              Unable to fetch hospitals from the backend.
            </p>
            <p className="text-charcoal text-sm mb-4">
              Please ensure your backend server is running at: <code className="bg-mint-veil text-forest-ink px-2 py-1 rounded">{finalBackendUrl}</code>
            </p>
            <p className="text-forest-ink text-xs">
              Check the browser console for detailed error messages.
            </p>
          </div>
        )}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-forest-ink border-r-transparent" data-testid="loading-spinner"></div>
            <p className="text-charcoal mt-4">Loading hospitals...</p>
          </div>
        ) : filteredHospitals.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-charcoal">No hospitals found</p>
            <p className="text-charcoal mt-2">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" data-testid="hospitals-grid">
            {filteredHospitals.map((hospital, index) => {
              // All hospitals use Dialog - clicking card opens modal
              return (
                <div key={hospital.id} className="h-full">
                <Dialog onOpenChange={(open) => {
                  if (open) {
                    fetchDoctors(hospital.id);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Card 
                      className="h-full flex flex-col p-0 bg-cream-paper border border-sage-mist hover:border-forest-ink/40 transition-all duration-300 cursor-pointer group overflow-hidden relative z-10 rounded-[14px] text-left"
                      style={{ animationDelay: `${index * 100}ms` }}
                      data-testid={`hospital-card-${index}`}
                    >
                      <CardHeader className="relative z-10 mb-0 px-5 pt-5 pb-4 border-b border-mint-veil bg-keylime-wash">
                        <div className="flex items-start gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-forest-ink text-cream-paper text-sm font-semibold tracking-wide">
                            {hospitalInitials(hospital.name)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-forest-ink text-lg font-semibold leading-snug line-clamp-2 min-h-[2.75rem]">
                              {hospital.name}
                            </CardTitle>
                            <CardDescription className="text-charcoal flex items-center gap-1.5 mt-1.5">
                              <MapPin className="w-4 h-4 shrink-0 text-forest-ink" />
                              <span className="truncate">{hospital.city || 'Location not available'}</span>
                            </CardDescription>
                          </div>
                          {hospital.rating != null && (
                            <div className="shrink-0 bg-cream-paper border border-sage-mist px-2.5 py-1 rounded-full flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                              <span className="text-forest-ink text-sm font-medium">{hospital.rating}</span>
                            </div>
                          )}
                        </div>
                        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-forest-ink truncate min-h-[1rem]">
                          {hospital.designation || '\u00a0'}
                        </p>
                      </CardHeader>

                      <CardContent className="flex-1 flex flex-col gap-4 px-5 py-4 relative z-10">
                        <div className="flex items-center gap-2 text-charcoal">
                          <Building2 className="w-4 h-4 text-forest-ink" />
                          <span className="text-sm">Est. {hospital.established_year != null ? hospital.established_year : 'Not available'}</span>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-forest-ink font-semibold">Specialties</p>
                          <TagList items={hospital.specialties} max={2} />
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-forest-ink font-semibold">Cancer Types</p>
                          <TagList
                            items={(hospital.cancer_types || []).length > 0
                              ? hospital.cancer_types
                              : [hospital.category || hospital.designation].filter(Boolean)}
                            max={3}
                          />
                        </div>
                        <Button 
                          type="button"
                          className="mt-auto w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper rounded-xl transition-all duration-300 relative z-20"
                          data-testid={`view-details-${index}`}
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </DialogTrigger>

                <DialogContent className="flex flex-col gap-0 p-0 bg-cream-paper border-hair text-forest-ink w-[95vw] sm:w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl mx-2 sm:mx-4" data-testid="hospital-details-modal">
                  <DialogHeader className="shrink-0 px-5 sm:px-7 pt-6 pb-4 border-b border-hair text-left">
                    <DialogTitle className="text-forest-ink break-words pr-8">
                      {hospital.name}
                    </DialogTitle>
                    <DialogDescription className="text-charcoal flex items-center gap-2 mt-2 break-words">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <span>{hospital.city}, {hospital.address || 'Not available'}</span>
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-5">
                    {/* Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="rounded-2xl border border-hair bg-paper p-3.5 sm:p-4 flex items-center gap-2 sm:gap-3">
                        <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-forest-ink flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm text-charcoal">Phone</p>
                          {hospital.phone || hospital.contact ? (
                            <a 
                              href={`tel:${(hospital.phone || hospital.contact).replace(/\s|\(|\)|-/g, "")}`}
                              className="text-forest-ink font-semibold text-sm sm:text-base break-words hover:text-charcoal transition-colors"
                            >
                              {hospital.phone || hospital.contact}
                            </a>
                          ) : (
                            <p className="text-forest-ink font-semibold text-sm sm:text-base break-words">Not available</p>
                          )}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-hair bg-paper p-3.5 sm:p-4 flex items-center gap-2 sm:gap-3">
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-forest-ink flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm text-charcoal">
                            {hospital.contactEmail ? 'Email' : hospital.inquiryUrl ? 'Inquiry' : 'Email'}
                          </p>
                          {hospital.contactEmail ? (
                            <a 
                              href={`mailto:${hospital.contactEmail.split(",")[0].trim()}`}
                              className="text-forest-ink font-semibold text-sm sm:text-base break-words hover:text-charcoal transition-colors"
                            >
                              {hospital.contactEmail.split(",")[0].trim()}
                            </a>
                          ) : hospital.inquiryUrl ? (
                            <a 
                              href={hospital.inquiryUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-forest-ink font-semibold text-sm sm:text-base break-words hover:text-charcoal transition-colors"
                            >
                              Inquiry Form
                            </a>
                          ) : hospital.email ? (
                            <a 
                              href={`mailto:${hospital.email}`}
                              className="text-forest-ink font-semibold text-sm sm:text-base break-words hover:text-charcoal transition-colors"
                            >
                              {hospital.email}
                            </a>
                          ) : (
                            <p className="text-forest-ink font-semibold text-sm sm:text-base break-words">Not available</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const site = hospital.referral_route || hospital.website || hospital.detailsUrl || hospital.details_url || hospital.appointmentUrl;
                      const qs = new URLSearchParams();
                      if (hospital.id) qs.set('hospital_id', hospital.id);
                      if (selectedCancerType && selectedCancerType !== 'All') qs.set('cancer_name', selectedCancerType);
                      return (
                        <div className="space-y-3">
                          {site ? (
                            <>
                              <a
                                href={site}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 bg-forest-ink hover:bg-forest-shadow text-cream-paper font-normal py-2 px-4 rounded-[14px] transition-all duration-300 w-full"
                              >
                                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                                How to get referred here
                              </a>
                              <a
                                href={site}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 text-sm text-charcoal hover:text-forest-ink underline underline-offset-2"
                              >
                                See hospital site
                              </a>
                            </>
                          ) : (
                            <p className="text-sm text-charcoal leading-relaxed rounded-2xl border border-hair bg-mist/70 p-4">
                              Ask your oncologist for a referral to this centre.
                            </p>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full border-border-mist text-forest-ink hover:bg-mint-veil"
                            onClick={() => navigate(`/get-started?${qs.toString()}`)}
                          >
                            Get Started
                          </Button>
                        </div>
                      );
                    })()}

                    <HospitalFacts hospital={hospital} />

                    <LiveSignals signals={hospital.live_signals} />

                    {hospital.rating != null ? (
                    <div className="rounded-2xl border border-hair bg-paper p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                          <span className="text-2xl font-light text-forest-ink">{hospital.rating}</span>
                          <span className="text-charcoal">/ 5.0</span>
                        </div>
                        <div className="text-sm text-charcoal">
                          {hospital.total_reviews != null
                            ? `${hospital.total_reviews.toLocaleString()} reviews`
                            : 'Reviews not available'}
                        </div>
                      </div>
                    </div>
                    ) : (
                    <div className="rounded-2xl border border-hair bg-paper p-4">
                      <p className="text-sm text-charcoal">Rating</p>
                      <p className="text-forest-ink mt-1">Not available</p>
                    </div>
                    )}

                    <Tabs defaultValue="specialties" className="w-full">
                      <TabsList className="hospital-segment h-auto min-h-11 w-full grid grid-cols-4 gap-1 rounded-2xl bg-mist p-1 overflow-hidden border border-hair">
                        <TabsTrigger
                          value="specialties"
                          className="h-10 min-w-0 rounded-xl px-1 text-[11px] sm:text-sm font-medium"
                        >
                          <span className="truncate">Specialties</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="cancers"
                          className="h-10 min-w-0 rounded-xl px-1 text-[11px] sm:text-sm font-medium"
                        >
                          <span className="truncate sm:hidden">Types</span>
                          <span className="truncate hidden sm:inline">Cancer Types</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="facilities"
                          className="h-10 min-w-0 rounded-xl px-1 text-[11px] sm:text-sm font-medium"
                        >
                          <span className="truncate">Facilities</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="doctors"
                          className="h-10 min-w-0 rounded-xl px-1 text-[11px] sm:text-sm font-medium"
                        >
                          <span className="truncate">Doctors</span>
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="specialties" className="mt-5">
                        {(hospital.specialties || []).length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {(hospital.specialties || []).map((specialty, i) => (
                              <DetailTile key={i} icon={Stethoscope}>{specialty}</DetailTile>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-charcoal">Specialties are not yet listed for this centre.</p>
                        )}
                      </TabsContent>

                      <TabsContent value="cancers" className="mt-5 space-y-3">
                        {(hospital.coverage?.length > 0) && (
                          <div className="rounded-2xl border border-hair bg-mist/70 p-4 text-sm text-forest-ink leading-relaxed">
                            This centre treats {hospital.coverage.join(', ').toLowerCase()}.
                          </div>
                        )}
                        {(hospital.cancer_types || []).length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {(hospital.cancer_types || []).map((type) => (
                              <DetailTile key={type}>{type}</DetailTile>
                            ))}
                          </div>
                        ) : (hospital.coverage || []).length === 0 ? (
                          <p className="text-sm text-charcoal">Cancer-type detail not yet listed for this centre.</p>
                        ) : null}
                      </TabsContent>

                      <TabsContent value="facilities" className="mt-5">
                        {(hospital.facilities || []).length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {(hospital.facilities || []).map((facility, i) => (
                              <DetailTile key={i} icon={Building2}>{facility}</DetailTile>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-charcoal">Facilities are not yet listed for this centre.</p>
                        )}
                      </TabsContent>

                      <TabsContent value="doctors" className="mt-5" data-testid="doctors-list">
                        <DoctorsTab
                          data={doctorPayloads[hospital.id]}
                          loading={!doctorPayloads[hospital.id]}
                        />
                      </TabsContent>
                    </Tabs>
                  </div>
                </DialogContent>
              </Dialog>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default FindHospitals;
